import { ReactNode, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { Field, Form, Formik } from 'formik';
import { Switch, TextField } from 'formik-mui';
import { useTranslation } from "react-i18next";
import { FormControlLabel, LinearProgress } from '@mui/material';

import Settings from '../../data/settings';
import SignIn, { SignInProps, validateEmail } from './SignIn';
import { checkExistance, magicAccess } from '../../api';
import { DataLoader } from '../';
import { MagicAccessExtraParams } from '../../models/MagicAccessRequest';

enum States {
  checkExistance,
  existingUser,
  existingUserTypePassword,
  newUser,
  newUserSetPassword
}

export interface MagicAccessProps extends SignInProps {
  onMagicLinkRequested?: () => void;
}

interface MagicAccessValues {
  email: string;
}

interface Existance {
  exists: boolean;
  hasPassword?: boolean;
}

const MagicAccess = ({ onMagicLinkRequested, onSignedIn, onForgottenPassword }: MagicAccessProps) => {
  const { t } = useTranslation("translation");

  const [state, setState] = useState(States.checkExistance);
  const [existance, setExistance] = useState<Existance | null>(null);
  const [email, setEmail] = useState("");

  const [error, setError] = useState<string | ReactNode | undefined | null>(undefined);
  const [message, setMessage] = useState<string | ReactNode | undefined | null>(undefined);

  const onCheckExistance = async (values: MagicAccessValues) => {
    const response = await checkExistance(values.email);

    if (!response)
      setError(t("internet-disconnected"));
    else if (response.status !== 200)
      setError(t("internal-error"));
    else {
      if (response.data.exists)
        setState(States.existingUser);
      else
        setState(States.newUser);

      setExistance(response.data);
      setEmail(values.email);
    }
  }

  const checkExistanceForm = (<>
    <h1>{t('hello')}!</h1>
    <h2>{t('magic-link-need-email')}</h2>

    <Formik
      initialValues={{
        email: ''
      }}
      validate={validateEmail}
      onSubmit={async (values, { setSubmitting }) => {
        setError(undefined);
        setSubmitting(true);
        await onCheckExistance(values);
        setSubmitting(false);
      }}>
      {({ submitForm, isSubmitting }) => (
        <Form>
          <Box margin={1}>
            <Field
              component={TextField}
              name="email"
              type="email"
              label="Email"
              helperText={t("enter-email")}
            />
          </Box>

          <Box margin={1}>
            <Button
              className="signin-button"
              variant="contained"
              disabled={isSubmitting}
              onClick={submitForm}>
              {t("ok")}
            </Button>
          </Box>

          <LinearProgress style={{ marginTop: "1em", opacity: isSubmitting ? 1 : 0 }} />
        </Form>
      )}
    </Formik>
  </>);

  const onSendMagicLink = async (values?: MagicAccessExtraParams) => {
    setMessage(<DataLoader />);

    const onClickOkay = () => {
      setMessage(null);
      if (onMagicLinkRequested)
        onMagicLinkRequested();
    }

    const response = await magicAccess({ email: email, app_id: Settings.appId, ...values });

    if (!response || response.status !== 200)
      setError(t("internal-error"));
    else
      setMessage(<>
        <MarkEmailReadIcon className='success' />
        <h1>{t('check-your-email')}</h1>
        <p>{t('magic-link-requested', { email: email })}</p>

        <Button variant='contained' onClick={onClickOkay}>OK</Button>
      </>);
  }

  const existingUserForm = (<>
    <h1>{t('welcome-back')}</h1>

    {state === States.existingUser &&
      <>
        <h2>{t('magic-link-existing-user-desc')}</h2>

        <AutoFixHighIcon className='success' />

        <div className="action-box">
          <Button variant='contained' onClick={async () => await onSendMagicLink()}>{t('send-magic-link')}</Button>
        </div>
      </>}

    {state !== States.existingUserTypePassword && existance?.hasPassword &&
      <div className="action-box">
        <div className="link-button" onClick={() => setState(States.existingUserTypePassword)}>{t("or-type-password")}</div>
      </div>}

    {state === States.existingUserTypePassword &&
      <>
        <div className="action-box">
          <SignIn hideTitle initialValues={{ email: email }}
            onSignedIn={onSignedIn} onForgottenPassword={onForgottenPassword} />
        </div>

        <div className="action-box">
          <div className="link-button" onClick={() => setState(States.existingUser)}>{t("get-magic-link-instead")}</div>
        </div>
      </>}
  </>);

  const newUserForm = (<>
    <h1>{t('welcome-to', { project: Settings.project.title })}</h1>
    <h2>{t('magic-link-new-user-desc')}</h2>

    <AutoFixHighIcon className='success' />

    <Formik
      initialValues={{ allow_newsletter: false }}
      onSubmit={async (values) => await onSendMagicLink(values)}>
      {({ isSubmitting, handleSubmit }) => (
        <Form onSubmit={handleSubmit}>
          <FormControlLabel className="allow-newsletter"
            control={<Field component={Switch} type="checkbox" name="allow_newsletter" />}
            label={t('allow-newsletter')} />

          <div className="action-box">
            <Button type="submit" variant='contained'>{t('send-magic-link')}</Button>
          </div>

          {isSubmitting && <LinearProgress />}
        </Form>
      )}
    </Formik>
  </>);

  return <div className="auth-component">
    {state === States.checkExistance && checkExistanceForm}
    {(state === States.existingUser || state === States.existingUserTypePassword) && existingUserForm}
    {state === States.newUser && newUserForm}

    {message && <div className="overlay-message">{message}</div>}

    {error &&
      <Box marginBottom={3}>
        <p className="error-message">{error}</p>
      </Box>}

    {(state === States.existingUser || state === States.newUser) &&
      <Box margin={1}>
        <div className="link-button" onClick={() => setState(States.checkExistance)}>{t("cancel")}</div>
      </Box>}
  </div>;
}

export default MagicAccess;
